import { useCallback } from 'react';

import { useQueryUser } from '@heidi/workspace/data';

import { getPostNoteQualityPorts } from '../../../data/post-note-quality/post-note-quality-ports';
import {
  resolveImproveNotesTasks,
  type ImproveNotesTaskId,
} from '../improve-notes-panel/tasks.config';
import { useMutateMarkPostNoteQualityTask } from './use-mutate-post-note-quality';

const TOTAL_IMPROVE_NOTES_TASKS = 5;
/**
 * Reward milestones (task counts, create-note included) and the cumulative
 * extra trial days unlocked at each — mirrors the backend
 * PostNoteQualityRewardManager (+7 at 2 tasks, +14 at all 5).
 */
const REWARD_DAYS_BY_MILESTONE: Readonly<Record<number, number>> = {
  2: 7,
  5: 14,
};

/**
 * GRO-3270: central guard for the evidence-of-use signals that complete
 * "Improve your notes" tasks.
 *
 * Returns a stable callback that call sites fire when their feature was
 * successfully used (e.g. context saved, Ask Heidi stream completed). The
 * callback no-ops unless all of these hold:
 * - the post-note quality experiment puts the user in a sidebar arm
 *   (`IS_POST_NOTE_QUALITY_PANEL_ENABLED`, i.e. sidebar_only / sequential)
 * - the panel has not been permanently dismissed
 * - the task is not already completed
 *
 * Keeping the guards here means call sites stay one-liners with no
 * feature-flag or state awareness.
 */
export function useMarkImproveNotesTaskComplete(): (taskId: ImproveNotesTaskId) => void {
  const { data: userData } = useQueryUser();
  const { mutate: markTask } = useMutateMarkPostNoteQualityTask();
  const { trackTaskCompleted, trackTrialDaysUnlocked, trackExperienceCompleted } =
    getPostNoteQualityPorts().usePostNoteQualityAnalytics();

  const completedTasks = userData?.post_note_quality_completed_tasks;
  const dismissedAt = userData?.post_note_quality_panel_dismissed_at;
  const hasUser = Boolean(userData);

  return useCallback(
    (taskId: ImproveNotesTaskId) => {
      if (!getPostNoteQualityPorts().isPanelEnabled()) return;
      if (!hasUser || dismissedAt) return;
      if (completedTasks?.includes(taskId)) return;
      markTask(taskId);

      // GRO-3284 analytics. Completed count includes the implicit create-note,
      // so it matches the backend reward milestones. Each call marks exactly
      // one new task, so the count steps by 1 and crosses each milestone once.
      const completedCount = resolveImproveNotesTasks([
        ...(completedTasks ?? []),
        taskId,
      ]).filter((task) => task.completed).length;

      trackTaskCompleted(taskId, completedCount);

      const rewardDays = REWARD_DAYS_BY_MILESTONE[completedCount];
      if (rewardDays !== undefined) {
        trackTrialDaysUnlocked(rewardDays, completedCount);
      }

      if (completedCount === TOTAL_IMPROVE_NOTES_TASKS) {
        trackExperienceCompleted(completedCount);
      }
    },
    [
      hasUser,
      dismissedAt,
      completedTasks,
      markTask,
      trackTaskCompleted,
      trackTrialDaysUnlocked,
      trackExperienceCompleted,
    ]
  );
}
